/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, HostBinding, computed, input, model, output, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UIColor } from '@core/ui';
import { BooleanInputType, toBooleanProperty } from '@utils/boolean';
import { stopAndPreventEvent } from '@utils/dom/event';
import { SlideCheckboxEvent } from './slide-checkbox.event';

const idPrefix = 'mir-slide-checkbox';
let nextUniqueId = 0;

const TW_Disable = 'pointer-events-none opacity-60';

export type SlideCheckboxLabelJustificationType = 'start' | 'center' | 'end' | 'between';
const JustifyToCssJustify = {
  'start': 'flex-start',
  'center': 'center',
  'end': 'flex-end',
  'between': 'space-between',
} as const;


@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'SlideCheckbox',
  templateUrl: './slide-checkbox.component.html',
  styleUrls: ['./slide-checkbox.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule
  ],
})

export class SlideCheckbox /*implements ControlValueAccessor*/ {

  @HostBinding('class')
  get hostCssClass(): string {
    return `${this.disabled() ? TW_Disable : ''}`;
  }

  @HostBinding('style.--toggle-checkbox-color')
  get slideCheckboxColorStyle(): string { return `var(--mir-color-${this.color()})`; }

  @HostBinding('style.--toggle-label-justify')
  get labelJustifyStyle(): string { return JustifyToCssJustify[this.labelJustify()]; }

  @HostBinding('attr.id')
  get hostID(): string { return this.id() ?? this.#uniqueId(); }


  /** @Input */
  readonly id = input<string>();
  readonly color = input<UIColor>('gray-900');

  readonly checked = model(false);
  readonly disabled = model(false);

  readonly highlightLabel = input(false, { transform: (value: BooleanInputType) => toBooleanProperty(value) });
  readonly labelFirst = input(false, { transform: (value: BooleanInputType) => toBooleanProperty(value) });
  readonly labelFullWidth = input(false, { transform: (value: BooleanInputType) => toBooleanProperty(value) });
  readonly labelJustify = input<'start' | 'center' | 'end' | 'between'>('start');

  /** @Output */
  readonly change = output<SlideCheckboxEvent>();


  protected readonly inputId = computed(() => {
    return `${this.id() ?? this.#uniqueId()}-mir-slide-checkbox-input`;
  });

  onChange: any = () => { };
  onTouch: any = () => { };

  #uniqueId = signal(`${idPrefix}-${++nextUniqueId}`);

  constructor() { }

  handleInputClick() {
    const checked = !this.checked();
    this.checked.set(checked);
    this.change.emit({
      source: this,
      checked,
    });
  }

  handleInteractionEvent(event: Event) {
    stopAndPreventEvent(event);
  }

  toggle() {
    const checked = !this.checked();
    this.checked.set(checked);
    this.onChange(checked);
  }

  disable() {
    this.disabled.set(true);
  }

  enable() {
    this.disabled.set(false);
  }
}
